import {createSelector} from 'reselect'

import moment from 'moment'
import {maxBy, sumBy} from 'lodash'

      // var taskNameSelector = (task) => {
      //   var name = activity.Name
      //   if (name.length > 25) name = name.substring(0, 23) + '...'
      //   name += '(' + activity.UniqueId
      //   if (activity.Tasks && activity.Tasks.length > 1) name += ':' + task.UniqueId
      //   name += ')'
      //   return {
      //     name: name
      //   }
      // }

// Metadata selectors
export let statusDateSelector = createSelector(
  dataset => (dataset.status_date),
  status_date => (moment(status_date))
)

export let projectInfoSelector = createSelector(
  dataset => dataset.title,
  dataset => dataset.subtitle,
  dataset => dataset.data_omitted,
  statusDateSelector,
  (title, subtitle, data_omitted, status_date) => ({
    title,
    subtitle,
    data_omitted,
    status_date
  })
)

// Upload state selectors
// (dataset) => (bool)
export let isUploadingSelector = createSelector(
  dataset => (dataset.upload_state), 
  upload_state => (upload_state == 'uploading')
)

// (dataset) => (bool)
export let isFailedUploadSelector = createSelector(
  dataset => (dataset.upload_state),
  upload_state => (upload_state == 'failure')
)

// (dataset, current_time) => (bool)
export let isRecentlyUploadedSelector = createSelector(
  dataset => (dataset.upload_state),
  dataset => (moment(dataset.uploaded_at)),
  (dataset, current_time) => (moment(current_time).startOf('minute')),
  (upload_state, uploaded_at, current_min) =>{
    return (upload_state == 'success' && current_min.diff(uploaded_at, 'minutes') < 2)
  }

)

export let publicIdSelector = createSelector(
  dataset => (dataset.public_guid),
  public_guid => (new Buffer(public_guid)).toString('base64')
)

export let isSharedSelector = createSelector(
  dataset => (dataset.public_guid),
  public_guid => (!!public_guid)
)

/**
 *  Flatten Activities/Tasks into one 'acttask' array
 * 
 * [{acttask_id, name, start, finish, duration, status, critical, resource_cost,fixed_cost, total_cost, wbs}]
 *
 * Date values are moment objects
 * Status is a string (switch to Enum ?)
 * 
*/

const started_ahead = 'STARTED_AHEAD_OF_SCHEDULE'
const started_behind = 'STARTED_BEHIND_SCHEDULE'
const notstarted_ahead = 'NOT_STARTED_ON_SCHEDULE'
const notstarted_behind = 'NOT_STARTED_BEHIND_SCHEDULE'
const complete = 'COMPLETE' 

// (dataset) -> [{acttask_t}]
export let acttasksSelector = createSelector(
  dataset => (dataset.data),
  statusDateSelector,
  (data, status_date) => {
    if (!data) return []

    let activities = data.Activities

    let acttasks = []

    for (var i in activities) {
      var activity = activities[i]
      
      for (var j in activity.Tasks) {
        var task = activity.Tasks[j]
        
        if (!task.Summary) 
        {
          let name = activity.Name
          if (activity.Tasks && activity.Tasks.length > 1) name += `[${j}]`

          acttasks.push({
            acttask_id: `${activity.UniqueId}:${task.UniqueId}`,
            name,
            start: moment(task.StartDate),
            finish: moment(task.FinishDate),
            duration: task.Duration,
            status: task.Status,
            critical: task.Critical,
            resource_cost: task.ResourceCost,
            fixed_cost: task.FixedCost,
            total_cost: task.ResourceCost + task.FixedCost,
            wbs: activity.Wbs,
            is_behind: task.Status == 'STARTED_BEHIND_SCHEDULE' || task.Status == 'NOT_STARTED_BEHIND_SCHEDULE',
            is_ahead: moment(task.FinishDate) > status_date && (task.Status == 'STARTED_AHEAD_OF_SCHEDULE' || task.Status == 'COMPLETE'),
            is_effort_driven: !task.Resources || !task.Resources.length
          })
        }
      }
    }

    return acttasks
  }  
)

// (dataset) -> [{}]
export let acttaskByStatusSelector = createSelector(
  acttasksSelector,
  (acttasks) => (
    acttasks.reduce((results, acttask) => {
      switch(acttask.status)
      {
        case started_ahead:
          results.started_onschedule.all.push(acttask)
          acttask.critical && results.started_onschedule.critical.push(acttask)
          !acttask.critical && results.started_onschedule.non_critical.push(acttask)
          break
        case started_behind: 
          results.started_behindschedule.all.push(acttask)
          acttask.critical && results.started_behindschedule.critical.push(acttask)
          !acttask.critical && results.started_behindschedule.non_critical.push(acttask)
          break
        case notstarted_ahead: 
          results.notstarted_onschedule.all.push(acttask)
          acttask.critical && results.notstarted_onschedule.critical.push(acttask)
          !acttask.critical && results.notstarted_onschedule.non_critical.push(acttask)
          break
        case notstarted_behind:
          results.notstarted_behindschedule.all.push(acttask)
          acttask.critical && results.notstarted_behindschedule.critical.push(acttask)
          !acttask.critical && results.notstarted_behindschedule.non_critical.push(acttask)
          break
        case complete:
          results.complete.all.push(acttask)
          acttask.critical && results.complete.critical.push(acttask)
          !acttask.critical && results.complete.non_critical.push(acttask)
          break
      }

        return results
    }, 
    {
      complete: {
        all: [],
        critical: [],
        non_critical: []
      },
      started_onschedule: {
        all: [],
        critical: [],
        non_critical: []
      },
      notstarted_onschedule: {
        all: [],
        critical: [],
        non_critical: []
      },
      started_behindschedule: {
        all: [],
        critical: [],
        non_critical: []
      },
      notstarted_behindschedule: {
        all: [],
        critical: [],
        non_critical: []
      }
    })
  )
)

// (dataset, current_date) => {in_progress, next_seven, next_fourteen, fourteen_plus}
export let acttaskByStartDateSelector = createSelector(
  acttasksSelector,
  (dataset, current_date) => moment(current_date).startOf('day'),
  (acttasks, current_date) => (
    acttasks.reduce((results, acttask) =>  {
      let daysToTask = acttask.start.diff(current_date, 'days')

      if (acttask.status != complete && daysToTask < 0)
      {
        results.in_progress.push(acttask)
      }
      else if (daysToTask >= 0 && daysToTask < 7)
      {
        results.next_seven.push(acttask)
      }
      else if (daysToTask >= 7 && daysToTask < 14)
      {
        results.next_fourteen.push(acttask)
      }
      else if (daysToTask >= 14)
      {
        results.fourteen_plus.push(acttask)
      }
      
      return results
    },
    {
      in_progress: [],
      next_seven: [],
      next_fourteen: [],
      fourteen_plus: []
    })
  )
)

export let tasksWithCostSelector = createSelector(
  acttasksSelector,
  acttasks => (acttasks.filter(acttask => acttask.total_cost > 0))
)
export let acttaskByCostSelector = createSelector(
  tasksWithCostSelector,
  acttasks => (acttasks.reduce((results, acttask) => {
      if (acttask.resource_cost > 0 ) results.resource_cost.push(acttask)
      if (acttask.fixed_cost > 0 ) results.fixed_cost.push(acttask)
      return results
    }
    , {fixed_cost: [], resource_cost: []}))
)

export let hasCostDataSelector = createSelector(
  acttaskByCostSelector,
  by_cost => (by_cost.resource_cost.length > 0 || by_cost.fixed_cost.length > 0)
)
/**
 * Collection name selectors 
 * 
 * "Project is", vs "Tasks are" 
 *
 */

 let isPartial = dataset => (dataset.data_omitted || dataset.subtitle != '')

 // (dataset) -> String
 export let collectionNameSelector = createSelector(
   isPartial,
   is_partial => (is_partial ? 'Tasks' : 'Project')
 )

 export let collectionNameIsSelector = createSelector(
   isPartial,
   is_partial => (is_partial ? 'Tasks are' : 'Project is')
 )

 export let collectionNameHaveNot = createSelector(
   isPartial,
   is_partial => (is_partial ? 'Tasks have not' : 'Project have not')   
 )

/**
 * Totals
 * 
 */
export let lastFinishDateSelector = createSelector(
  acttasksSelector,
  acttasks => maxBy(acttasks, acttask => (acttask.finish)).finish
)
export let totalCostSelector = createSelector(
  acttasksSelector,
  acttasks => sumBy(acttasks, acttask => (acttask.total_cost))
)


/**
 * 
 * 
 */
// SHZ TODO Furthest Ahead and Furthest Behind need percent_used values 
export let furthestAheadSelector = createSelector(
  acttasksSelector,
  acttasks => false
)
export let furthestBehindSelector = createSelector(
  acttasksSelector,
  acttasks => false
)
export let mostCriticalSelector = createSelector(
  acttasksSelector,
  acttasks => false
)
export let mostExpensiveSelector = createSelector(
  acttasksSelector,
  (acttasks) => {
    let max = maxBy(acttasks, acttask => (acttask.total_cost))
    return max.total_cost > 0 ? max : false
  }
)


// WEBPACK FOOTER //
// ./src/selectors/dataset.js